// The page's photogrammetry worker (pgworker.ts): started on first use and kept (it holds the frames' features),
// each frame's features sent once, cameras every job; a job's progress and rounds come back as it runs.
import pgWorkerSrc from 'worker:src/photo/pgworker.ts';
import type { PG } from './jobs.ts';
import type { FrameFeats } from './feats.ts';
import type { PGIn, PGOut } from './pgworker.ts';

export function pgHost(): PG {
  let w: Worker | null = null, url = '', seq = 0, sent = new WeakSet<FrameFeats>();
  const worker = () => {
    if (w) return w;
    url ||= URL.createObjectURL(new Blob([pgWorkerSrc], { type: 'text/javascript' }));
    w = new Worker(url);
    sent = new WeakSet();
    w.onerror = e => { e.preventDefault(); w?.terminate(); w = null; };
    return w;
  };
  const post = (m: PGIn) => worker().postMessage(m);
  return {
    load(fs) {
      const fresh = fs.filter(f => !sent.has(f.ff));
      if (fresh.length) post({ load: fresh });   // copied (the page keeps its own to draw)
      for (const f of fresh) sent.add(f.ff);
      post({ cams: fs.map(f => f.cam) });
    },
    run(job, prog, stop, iter, finish) {
      const wk = worker(), id = ++seq;
      return new Promise((ok, fail) => {
        const on = (e: MessageEvent<PGOut>) => {
          const m = e.data;
          if (m.id !== id) return;
          if ('prog' in m) prog(m.prog);
          else if ('iter' in m) iter?.(m.iter);
          else { wk.removeEventListener('message', on); if ('error' in m) fail(new Error(m.error)); else ok(m.done as never); }
        };
        wk.addEventListener('message', on);
        stop.addEventListener('abort', () => wk.postMessage({ stop: id, how: 'cancel' } satisfies PGIn), { once: true });
        finish?.addEventListener('abort', () => wk.postMessage({ stop: id, how: 'finish' } satisfies PGIn), { once: true });
        wk.postMessage({ run: id, job } satisfies PGIn);
      });
    },
  };
}
